import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { OutreachDetailPage } from '@apps/web/components/outreaches/outreach_detail_page';
import { useDeepLinkOrganization } from '@apps/web/hooks/use_deep_link_organization';
import { claimSlackWebCollaborationDeepLink } from '@apps/web/lib/slack_web_claim_deep_link';

const outreachDetailsSearchSchema = z.object({
    openChat: z.boolean().optional(),
    chatId: z.string().uuid().optional(),
    chatMode: z.enum(['fullscreen']).optional(),
    slackWebClaim: z.string().optional(),
    contactAttemptId: z.string().optional(),
    outreachAction: z.enum(['assign_and_fill']).optional(),
    organizationId: z.string().optional(),
    mode: z.enum(['live', 'sandbox']).optional(),
});

export const Route = createFileRoute('/_app/(main)/outreaches/$callout-id')({
    head: () => ({
        meta: [
            {
                title: 'Open Shift Details | Phoebe',
            },
        ],
    }),
    validateSearch: outreachDetailsSearchSchema,
    component: OutreachDetailRouteComponent,
});

function OutreachDetailRouteComponent() {
    const { 'callout-id': outreachId } = Route.useParams();
    const navigate = Route.useNavigate();
    const {
        openChat,
        chatId,
        chatMode,
        slackWebClaim,
        contactAttemptId,
        outreachAction,
        organizationId: deepLinkOrganizationId,
        mode: deepLinkMode,
    } = Route.useSearch();
    const { isWaitingForDeepLinkOrganization } = useDeepLinkOrganization(
        deepLinkOrganizationId,
        deepLinkMode,
    );
    const [claimedSlackChat, setClaimedSlackChat] = useState<{
        readonly key: string;
        readonly chatId: string;
        readonly chatMode?: 'fullscreen';
        readonly outreachId: string;
    } | null>(null);
    const slackClaimKey = useMemo(() => {
        if (!chatId || !slackWebClaim) {
            return null;
        }
        return `${chatId}:${deepLinkOrganizationId ?? ''}:${slackWebClaim}`;
    }, [chatId, deepLinkOrganizationId, slackWebClaim]);

    useEffect(() => {
        setClaimedSlackChat(null);
    }, [outreachId]);

    useEffect(() => {
        if (!chatId || !slackWebClaim) {
            return;
        }
        if (isWaitingForDeepLinkOrganization || slackClaimKey === null) {
            return;
        }

        let isCancelled = false;

        void (async () => {
            const canOpenDeepLinkedChat =
                await claimSlackWebCollaborationDeepLink({
                    chatId,
                    organizationId: deepLinkOrganizationId ?? null,
                    slackWebClaim,
                });
            if (isCancelled) {
                return;
            }
            setClaimedSlackChat(
                canOpenDeepLinkedChat
                    ? { key: slackClaimKey, chatId, chatMode, outreachId }
                    : null,
            );
            if (canOpenDeepLinkedChat) {
                removeClaimedSlackChatParamsFromUrl();
            }
        })();

        return () => {
            isCancelled = true;
        };
    }, [
        chatId,
        chatMode,
        outreachId,
        deepLinkOrganizationId,
        isWaitingForDeepLinkOrganization,
        slackClaimKey,
        slackWebClaim,
    ]);

    const claimedChatIdForCurrentOutreach =
        claimedSlackChat?.outreachId === outreachId
            ? claimedSlackChat.chatId
            : undefined;
    const chatIdForDetail =
        chatId && slackWebClaim
            ? claimedSlackChat?.key === slackClaimKey
                ? claimedSlackChat.chatId
                : undefined
            : (chatId ?? claimedChatIdForCurrentOutreach);
    const chatModeForDetail = chatIdForDetail
        ? (chatMode ??
          (claimedSlackChat?.outreachId === outreachId
              ? claimedSlackChat.chatMode
              : undefined))
        : undefined;
    const [autoOpenChatRequestOutreachId, setAutoOpenChatRequestOutreachId] =
        useState<string | null>(() => (openChat ? outreachId : null));

    useEffect(() => {
        if (openChat) {
            setAutoOpenChatRequestOutreachId(outreachId);
            return;
        }
        setAutoOpenChatRequestOutreachId((currentRequestOutreachId) =>
            currentRequestOutreachId === outreachId
                ? currentRequestOutreachId
                : null,
        );
    }, [outreachId, openChat]);

    useEffect(() => {
        if (!openChat || isWaitingForDeepLinkOrganization) {
            return;
        }

        void navigate({
            search: (previousSearch) => ({
                ...previousSearch,
                openChat: undefined,
            }),
            replace: true,
        });
    }, [isWaitingForDeepLinkOrganization, navigate, openChat]);

    if (isWaitingForDeepLinkOrganization) {
        return null;
    }

    return (
        <OutreachDetailPage
            autoOpenChatOnLoad={autoOpenChatRequestOutreachId === outreachId}
            chatId={chatIdForDetail}
            {...(chatModeForDetail ? { chatMode: chatModeForDetail } : {})}
            outreachAction={outreachAction}
            outreachId={outreachId}
            selectedContactAttemptId={contactAttemptId}
        />
    );
}

function removeClaimedSlackChatParamsFromUrl() {
    const params = new URLSearchParams(window.location.search);
    params.delete('chatId');
    params.delete('agentConversationId');
    params.delete('chatMode');
    params.delete('slackWebClaim');
    const newUrl =
        window.location.pathname +
        (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
}
